import { useState, useEffect } from "react";
import { X, Send, Paperclip } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const isImg = (u) => /\.(png|jpe?g|webp|gif)$/i.test(u || "") || (u || "").startsWith("data:image") || (u || "").includes("/uploads/");

export const CustomOrderDetail = ({ request: r, role, onClose, onReload }) => {
  const [msg, setMsg] = useState("");
  const [img, setImg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  // Mark read on open + live-refresh the thread every 6s so new replies appear without a manual refresh.
  useEffect(() => {
    if (!r?.request_id) return;
    const refresh = async () => { try { await api.post(`/marketplace/custom/${r.request_id}/read`); await onReload?.(); } catch { /* ignore */ } };
    refresh();
    const id = setInterval(refresh, 6000);
    return () => clearInterval(id);
  }, [r?.request_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!r) return null;

  const attach = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Use JPG, PNG or WebP"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large. Maximum 10 MB."); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const res = await api.post("/marketplace/product/image/upload", fd); setImg(res.data.url); }
    catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const send = async () => {
    if (!msg.trim() && !img) return;
    setSending(true);
    try {
      await api.post(`/marketplace/custom/${r.request_id}/message`, { text: msg.trim(), image: img });
      setMsg(""); setImg(null);
      await onReload?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const history = (r.history || []).filter((h) => h.status || h.amount || h.note);
  const messages = r.messages || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="custom-order-detail">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-start justify-between">
          <div>
            <p className="text-base font-bold text-slate-900">{r.number}</p>
            <p className="text-xs text-slate-500">{r.product_name} · {role === "seller" ? (r.customer_name || r.customer_email) : "Your custom order"}</p>
          </div>
          <button onClick={onClose} data-testid="cod-close" className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>

        <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-2 text-xs text-slate-600" data-testid="cod-requirements">{r.requirements}</p>
        {r.quote_amount && <p className="mt-1 text-xs text-slate-500">Quote: <b>₹{r.quote_amount}</b>{r.payment_status === "paid" ? " · Paid ✓" : ""}{r.fulfilment_status ? ` · ${r.fulfilment_status}` : ""}</p>}

        {r.files?.length > 0 && (
          <div className="mt-3" data-testid="cod-gallery">
            <p className="mb-1 text-xs font-semibold text-slate-700">Reference gallery</p>
            <div className="flex flex-wrap gap-2">
              {r.files.map((f, i) => (
                <a key={i} href={f.url} target="_blank" rel="noreferrer noopener noreferrer" className="block" data-testid={`cod-file-${i}`}>
                  {isImg(f.url)
                    ? <img src={f.url} alt={f.name || `ref ${i + 1}`} className="h-20 w-20 rounded-lg border border-slate-200 object-cover" />
                    : <span className="inline-flex h-20 w-20 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-1 text-center text-[10px] text-sky-600 underline">{f.name || `File ${i + 1}`}</span>}
                </a>
              ))}
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-3" data-testid="cod-history">
            <p className="mb-1 text-xs font-semibold text-slate-700">Quote &amp; status history</p>
            <ul className="space-y-1">
              {history.map((h, i) => (
                <li key={i} className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span className="font-semibold capitalize text-slate-700">{(h.status || "update").replace(/_/g, " ")}</span>
                  {h.amount ? <span className="text-violet-600">₹{h.amount}</span> : null}
                  {h.note ? <span className="italic">“{h.note}”</span> : null}
                  <span className="ml-auto">{h.by} · {new Date(h.at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-3" data-testid="cod-chat">
          <p className="mb-1 text-xs font-semibold text-slate-700">Messages</p>
          <div className="max-h-52 space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-2">
            {messages.length === 0 ? <p className="text-center text-[11px] text-slate-400" data-testid="cod-chat-empty">No messages yet. Start the conversation below.</p> : messages.map((m, i) => {
              const mine = m.by === role;
              return (
                <div key={m.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-xs ${mine ? "bg-violet-600 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200"}`}>
                    {m.image && <a href={m.image} target="_blank" rel="noreferrer noopener noreferrer"><img src={m.image} alt="attachment" className="mb-1 max-h-40 rounded-lg" data-testid={`cod-msg-image-${i}`} /></a>}
                    {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
                    <p className={`mt-0.5 text-[9px] ${mine ? "text-violet-200" : "text-slate-400"}`}>{m.name || m.by} · {new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {img && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-slate-100 p-1.5 text-xs" data-testid="cod-pending-image">
              <img src={img} alt="pending" className="h-10 w-10 rounded object-cover" />
              <span className="text-slate-500">Image attached</span>
              <button onClick={() => setImg(null)} className="ml-auto text-rose-500" data-testid="cod-remove-image">Remove</button>
            </div>
          )}
          <div className="mt-2 flex items-center gap-2">
            <label className="cursor-pointer rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-slate-50" data-testid="cod-attach">
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={attach} className="hidden" />
              <Paperclip className="h-4 w-4" />
            </label>
            <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={uploading ? "Uploading image…" : "Type a message…"} data-testid="cod-chat-input" className="flex-1 rounded-full border border-slate-200 px-3 py-2 text-sm outline-none" />
            <button onClick={send} disabled={sending || uploading || (!msg.trim() && !img)} data-testid="cod-chat-send" className="rounded-full brand-gradient-bg p-2 text-white disabled:opacity-50"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
};
